import type { FC } from "react"

import styles from "./DatasourceConfigTable.module.scss"
import DatasourceConfigRow from "./row/DatasourceConfigRow"

import {
	type FragmentType,
	getFragmentData,
	graphql,
} from "@/souhl_admin/gql/__generated__"
import type {
	DataSource,
	DataSourcePolicyWithText,
	DataSourceWithText,
} from "@/souhl_admin/gql/__generated__/graphql"

const datasourceConfigTableFragment = graphql(/* GraphQL */ `
	fragment DatasourceConfigTable on Salon {
		id
		...DatasourceConfigRow
	}
`)

type DatasourceConfigTableProps = {
	salons: readonly FragmentType<typeof datasourceConfigTableFragment>[]
	dataSourceColumns: DataSourceWithText[]
	policyOptions: readonly DataSourcePolicyWithText[]
	selectedSalonIds: Set<number>
	onSelectAll: (checked: boolean) => void
	onSelectSalon: (salonId: number, checked: boolean) => void
	onUpdateMemo: (salonId: number, memo: string | null) => Promise<void>
	onCopyPassword: (salonId: number, dataSource: DataSource) => Promise<void>
	isPasswordCopying: (salonId: number, dataSource: DataSource) => boolean
}

const DatasourceConfigTable: FC<DatasourceConfigTableProps> = ({
	salons: salonFragments,
	dataSourceColumns,
	policyOptions,
	selectedSalonIds,
	onSelectAll,
	onSelectSalon,
	onUpdateMemo,
	onCopyPassword,
	isPasswordCopying,
}) => {
	const salons = getFragmentData(datasourceConfigTableFragment, salonFragments)
	const allSelected =
		salons.length > 0 && selectedSalonIds.size === salons.length

	return (
		<div className={styles.tableWrapper}>
			<table className={styles.table}>
				<thead>
					<tr className={styles.headerRow}>
						<th className={styles.thCheckbox}>
							<input
								type="checkbox"
								checked={allSelected}
								onChange={(e) => onSelectAll(e.target.checked)}
							/>
						</th>
						<th className={styles.thId}>ID</th>
						<th className={styles.thName}>店舗名</th>
						<th className={styles.thPolicy}>現在の媒体設定</th>
						<th className={styles.thPolicy}>目標の媒体設定</th>
						<th className={styles.thDiff}>差分</th>
						<th className={styles.thMemo}>メモ</th>
						{dataSourceColumns.length > 0 && (
							<th className={styles.thSeparator}>
								<div className={styles.separatorLine} />
							</th>
						)}
						{dataSourceColumns.map((col) => (
							<th key={col.dataSource} className={styles.thDataSource}>
								{col.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{salons.map((salon) => (
						<DatasourceConfigRow
							key={salon.id}
							salon={salon}
							dataSourceColumns={dataSourceColumns}
							policyOptions={policyOptions}
							selected={selectedSalonIds.has(salon.id)}
							onSelectSalon={onSelectSalon}
							onUpdateMemo={onUpdateMemo}
							onCopyPassword={onCopyPassword}
							isPasswordCopying={isPasswordCopying}
						/>
					))}
				</tbody>
			</table>
		</div>
	)
}

export default DatasourceConfigTable
